import { Fragment, useEffect, useMemo, useState } from 'react'
import { Check, ChevronRight, Copy, Info, Save, Search, X } from 'lucide-react'
import api, { ehAdministrador } from '../services/api'
import { catalogoTelasWeb } from './Layout'

// Aba Opções do Cadastro de Usuários: permissões por tela DO WEB (não o menu do ERP), no
// modelo do FrotaTrack — Acesso / Inserir / Editar / Excluir. Grava em frota.usu_web_opcao.
// Usuário ainda sem nada gravado abre com o que o Tipo de Liberação libera hoje:
// 5 = só Vendas, 6 = Vendas + Compras, demais = tudo.

type Perm = { a: boolean; i: boolean; e: boolean; x: boolean }
const COLS: { k: keyof Perm; rotulo: string; dica: string }[] = [
  { k: 'a', rotulo: 'Acesso', dica: 'Vê a tela no menu e consegue abrir' },
  { k: 'i', rotulo: 'Inserir', dica: 'Pode incluir registros novos' },
  { k: 'e', rotulo: 'Editar', dica: 'Pode alterar registros' },
  { k: 'x', rotulo: 'Excluir', dica: 'Pode excluir / cancelar' },
]
const VAZIO: Perm = { a: false, i: false, e: false, x: false }
const TUDO: Perm = { a: true, i: true, e: true, x: true }

function modulosDoTiplib(tiplib: string | number | null | undefined): string[] | null {
  const t = Number(String(tiplib ?? '').trim())
  if (t === 5) return ['Vendas']
  if (t === 6) return ['Vendas', 'Compras']
  return null // todos
}

function permsPeloTiplib(tiplib: string | number | null | undefined) {
  const mods = modulosDoTiplib(tiplib)
  const m: Record<string, Perm> = {}
  catalogoTelasWeb().forEach(g => g.telas.forEach(t => {
    m[t.rota] = !mods || mods.includes(g.modulo) ? { ...TUDO } : { ...VAZIO }
  }))
  return m
}

const th: React.CSSProperties = { textAlign: 'center', padding: '0.55rem 0.5rem', whiteSpace: 'nowrap' }
const cel: React.CSSProperties = { textAlign: 'center', padding: '0.35rem 0.5rem', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' }
// "table input" do index.css estica qualquer input pra 100%/34px — checkbox precisa de tamanho fixo
const chk: React.CSSProperties = {
  width: 15, height: 15, padding: 0, margin: 0, border: 'none', cursor: 'pointer', accentColor: '#1b3f7b', verticalAlign: 'middle',
}
const btnBarra: React.CSSProperties = {
  padding: '0 0.65rem', height: 30, fontSize: '0.78rem', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 5,
}

export default function ArvoreOpcoesUsuario({ nome, tiplib, usuarios }: {
  nome: string; tiplib: string | null; usuarios: string[]
}) {
  const catalogo = useMemo(() => catalogoTelasWeb(), [])
  const [perms, setPerms] = useState<Record<string, Perm>>(() => permsPeloTiplib(tiplib))
  const [abertos, setAbertos] = useState<Set<string>>(new Set())
  const [busca, setBusca] = useState('')
  const [copiando, setCopiando] = useState(false)
  const [aviso, setAviso] = useState('')
  const [carregando, setCarregando] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [alterado, setAlterado] = useState(false)
  const [info, setInfo] = useState<{ configurado: boolean; atualizado_por: string | null; atualizado_em: string | null }>({ configurado: false, atualizado_por: null, atualizado_em: null })
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null)

  // Gravado → usa; nada gravado → parte do Tipo de Liberação
  async function lerPermissoes(usuario: string, tiplibUsuario: string | number | null) {
    const r = await api.get(`/erp/usuarios/${encodeURIComponent(usuario)}/permissoes-web`)
    if (!r.data.configurado) return { perms: permsPeloTiplib(tiplibUsuario), dados: r.data }
    const m: Record<string, Perm> = {}
    catalogoTelasWeb().forEach(g => g.telas.forEach(t => { m[t.rota] = r.data.perms[t.rota] ?? { ...VAZIO } }))
    return { perms: m, dados: r.data }
  }

  function carregar() {
    setCarregando(true); setAviso(''); setMsg(null); setAlterado(false)
    lerPermissoes(nome, tiplib)
      .then(({ perms, dados }) => { setPerms(perms); setInfo(dados) })
      .catch(e => setMsg({ tipo: 'erro', texto: e?.response?.data?.error || 'Erro ao carregar as permissões.' }))
      .finally(() => setCarregando(false))
  }

  useEffect(carregar, [nome, tiplib])

  async function salvar() {
    setSalvando(true); setMsg(null)
    try {
      const r = await api.put(`/erp/usuarios/${encodeURIComponent(nome)}/permissoes-web`, { perms })
      setMsg({ tipo: 'ok', texto: `Permissões gravadas (${r.data.telas} telas com acesso).` })
      window.dispatchEvent(new Event('frotaweb:permissoes')) // menu do usuário logado se atualiza na hora
      const { perms: p, dados } = await lerPermissoes(nome, tiplib)
      setPerms(p); setInfo(dados); setAlterado(false); setAviso('')
    } catch (e: any) {
      setMsg({ tipo: 'erro', texto: e?.response?.data?.error || 'Erro ao gravar as permissões.' })
    } finally {
      setSalvando(false)
    }
  }

  const termo = busca.trim().toUpperCase()
  const grupos = useMemo(() => catalogo
    .map((g, idx) => ({ ...g, id: `${g.modulo}|${g.grupo}|${idx}`, telas: termo ? g.telas.filter(t => t.nome.toUpperCase().includes(termo)) : g.telas }))
    .filter(g => g.telas.length > 0), [catalogo, termo])

  const set = (rota: string, p: Perm) => { setPerms(prev => ({ ...prev, [rota]: p })); setAlterado(true) }

  // Sem acesso não tem como inserir/editar/excluir; marcar qualquer um liga o acesso
  function alternar(rota: string, k: keyof Perm) {
    const p = perms[rota] ?? VAZIO
    const novo = { ...p, [k]: !p[k] }
    if (k === 'a' && !novo.a) Object.assign(novo, VAZIO)
    if (k !== 'a' && novo[k]) novo.a = true
    set(rota, novo)
  }

  function acessoGrupo(telas: { rota: string }[], marcar: boolean) {
    setPerms(prev => {
      const n = { ...prev }
      telas.forEach(t => { n[t.rota] = marcar ? { ...(prev[t.rota] ?? VAZIO), a: true } : { ...VAZIO } })
      return n
    })
    setAlterado(true)
  }

  function abrirFechar(id: string) {
    setAbertos(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s })
  }

  async function copiarDe(outro: string) {
    setCopiando(false)
    if (!outro) return
    try {
      const u = await api.get(`/erp/usuarios/${encodeURIComponent(outro)}`)
      const { perms: p } = await lerPermissoes(outro, u.data.usu_tiplib)
      setPerms(p); setAlterado(true)
      setAviso(`Permissões copiadas de ${outro}. Confira e clique em Salvar para gravar.`)
    } catch {
      setAviso(`Não foi possível ler as permissões de ${outro}.`)
    }
  }

  const totalTelas = catalogo.reduce((s, g) => s + g.telas.length, 0)
  const comAcesso = Object.values(perms).filter(p => p.a).length

  let moduloAnterior = ''

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.7rem' }}>
        <div style={{ position: 'relative', width: 250 }}>
          <Search size={15} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input value={busca} onChange={e => { setBusca(e.target.value); if (e.target.value.trim()) setAbertos(new Set(catalogo.map((g, i) => `${g.modulo}|${g.grupo}|${i}`))) }}
            placeholder="Buscar tela por nome..."
            style={{ border: '1px solid #d1d5db', borderRadius: 4, height: 32, fontSize: '0.85rem', padding: '4px 8px 4px 30px', width: '100%', boxSizing: 'border-box' }} />
        </div>
        <button type="button" className="btn btn-secondary" style={btnBarra}
          onClick={() => setAbertos(new Set(grupos.map(g => g.id)))}>Expandir tudo</button>
        <button type="button" className="btn btn-secondary" style={btnBarra}
          onClick={() => setAbertos(new Set())}>Recolher tudo</button>
        <div style={{ position: 'relative' }}>
          <button type="button" className="btn btn-secondary" onClick={() => setCopiando(c => !c)}
            style={{ ...btnBarra, color: '#1b3f7b', borderColor: '#93c5fd' }}>
            <Copy size={13} /> Copiar de outro usuário
          </button>
          {copiando && (
            <select autoFocus size={8} onChange={e => copiarDe(e.target.value)} onBlur={() => setCopiando(false)}
              style={{ position: 'absolute', top: '110%', left: 0, zIndex: 20, minWidth: 200, border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.83rem', boxShadow: '0 6px 16px rgba(0,0,0,.12)' }}>
              {usuarios.filter(n => n !== nome).map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          )}
        </div>
        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#64748b' }}>
          <b style={{ color: '#1b3f7b' }}>{comAcesso}</b> de {totalTelas} telas com acesso
        </span>
        {alterado && (
          <button type="button" className="btn btn-secondary" style={btnBarra} disabled={salvando} onClick={carregar}>Descartar</button>
        )}
        {ehAdministrador() && (
          <button type="button" className="btn btn-primary" style={{ ...btnBarra, fontWeight: 600 }} disabled={salvando || carregando || (!alterado && info.configurado)} onClick={salvar}>
            <Save size={13} /> {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        )}
      </div>

      {msg && <div className={`alert ${msg.tipo === 'ok' ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '0.6rem', fontSize: '0.8rem' }}>{msg.texto}</div>}

      {(aviso || !info.configurado) ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '0.4rem 0.6rem', marginBottom: '0.6rem' }}>
          <Info size={14} />
          {aviso || `Ainda não configurado — marcado conforme o Tipo de Liberação (${String(tiplib ?? '').trim() || '-'}). Ao salvar, passa a valer o que estiver aqui.`}
        </div>
      ) : (
        <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
          Gravado{info.atualizado_por ? ` por ${info.atualizado_por}` : ''}{info.atualizado_em ? ` em ${info.atualizado_em}` : ''}.
        </div>
      )}

      <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ maxHeight: 480, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ ...th, textAlign: 'left', paddingLeft: '0.9rem' }}>Tela</th>
                {COLS.map(c => <th key={c.k} style={{ ...th, width: 90 }} title={c.dica}>{c.rotulo}</th>)}
                <th style={{ ...th, width: 80 }} title="Marca ou desmarca as quatro de uma vez">Todos</th>
              </tr>
            </thead>
            <tbody>
              {grupos.length === 0 && (
                <tr><td colSpan={6} style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af' }}>Nenhuma tela encontrada.</td></tr>
              )}
              {grupos.map(g => {
                const aberto = abertos.has(g.id)
                const qtdAcesso = g.telas.filter(t => perms[t.rota]?.a).length
                const novoModulo = g.modulo !== moduloAnterior
                moduloAnterior = g.modulo
                return (
                  <Fragment key={g.id}>
                    {novoModulo && (
                      <tr><td colSpan={6} style={{ padding: '0.55rem 0.9rem 0.3rem', fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#fff', borderBottom: 'none' }}>
                        {g.modulo}
                      </td></tr>
                    )}
                    <tr style={{ background: '#f8fbff' }}>
                      <td style={{ ...cel, textAlign: 'left', padding: '0.5rem 0.9rem' }}>
                        <button type="button" onClick={() => abrirFechar(g.id)}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: 0, color: '#1b3f7b', fontWeight: 700, fontSize: '0.86rem' }}>
                          <ChevronRight size={15} style={{ transform: aberto ? 'rotate(90deg)' : undefined, transition: 'transform .12s' }} />
                          {g.grupo}
                          <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.75rem' }}>({g.telas.length})</span>
                        </button>
                      </td>
                      <td style={cel}>
                        <input type="checkbox" checked={qtdAcesso === g.telas.length} title="Acesso a todas as telas do grupo"
                          ref={el => { if (el) el.indeterminate = qtdAcesso > 0 && qtdAcesso < g.telas.length }}
                          onChange={() => acessoGrupo(g.telas, qtdAcesso < g.telas.length)} style={chk} />
                      </td>
                      <td style={cel} colSpan={4} />
                    </tr>
                    {aberto && g.telas.map(t => {
                      const p = perms[t.rota] ?? VAZIO
                      return (
                        <tr key={t.rota} className="perm-linha">
                          <td style={{ ...cel, textAlign: 'left', paddingLeft: '2.4rem', color: p.a ? '#111827' : '#9ca3af' }}>{t.nome}</td>
                          {COLS.map(c => (
                            <td key={c.k} style={cel}>
                              <input type="checkbox" checked={p[c.k]} onChange={() => alternar(t.rota, c.k)}
                                style={chk} />
                            </td>
                          ))}
                          <td style={cel}>
                            <span style={{ display: 'inline-flex', gap: 8 }}>
                              <button type="button" title="Marcar todas" onClick={() => set(t.rota, { ...TUDO })}
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: '#16a34a', display: 'flex' }}><Check size={16} /></button>
                              <button type="button" title="Desmarcar todas" onClick={() => set(t.rota, { ...VAZIO })}
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: '#dc2626', display: 'flex' }}><X size={16} /></button>
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
      <style>{`.perm-linha:hover td{background:#f8fafc}`}</style>
    </div>
  )
}
